<script>
import {
  defineComponent, reactive, ref, onMounted,
} from 'vue';
import { mask } from 'vue-the-mask';
import { ElMessage } from 'element-plus';
import { adminSignIn } from '@/graphs/gql';
import shared from '../shared';
import lowercaseEmail from '@/directives/lowercase-email.js';

// Cache implementation
export default defineComponent({
  name: 'Authenticator',
  directives: { mask, lowercaseEmail },
  setup() {
    const {
      route,
      router,
      store,
      makeGraphRequest,
    } = shared();
    const item = reactive({
      email: '',
      password: '',
    });
    const loading = ref(false);
    const refForm = ref(null);
    const error = ref(false);
    const emailInput = ref(null);
    const passwordInput = ref(null);
    const { redirect } = route.query;

    // Пустые правила для форм
    const rules = {};

    function checkAuth() {
      if (store.getters['users/isMeSignedIn']) {
        if (route.path !== '/') router.push('/');
      }
    }

    async function submitForm() {
      try {
        if (!item.email || !item.password) {
          error.value = true;
          return;
        }

        loading.value = true;
        error.value = false;
        const response = await makeGraphRequest('mutation', adminSignIn, { input: item }, null);
        store.commit('users/SET_IS_ME_SIGNED_IN', true);
        store.commit('users/SET_ME', response.data.adminSignIn);
        
        // Redirect to the intended destination
        if (redirect) {
          router.replace(redirect);
        } else if (store.getters['users/isOrgAdmin']) {
          router.push('/manage/orgs');
        } else {
          router.push('/');
        }
      } catch (err) {
        error.value = true;

        // Extract error message from GraphQL response
        let errorMessage = 'Login failed. Please check your credentials and try again.';
        if (err.graphQLErrors && err.graphQLErrors.length > 0) {
          errorMessage = err.graphQLErrors[0].message;
        } else if (err.message) {
          errorMessage = err.message;
        }

        ElMessage({
          message: errorMessage,
          type: 'error',
          duration: 5000,
        });
      } finally {
        loading.value = false;
      }
    }

    function validateForm() {
      submitForm();
    }

    function gotoForgotPassword() {
      router.push('/auth/forgot-password?returnTo=cms');
    }

    onMounted(() => checkAuth());

    return {
      refForm,
      rules,
      loading,
      error,
      item,
      emailInput,
      passwordInput,
      validateForm,
      gotoForgotPassword,
    };
  },
});
</script>

<template>
  <el-form
    class="c-login-form"
    ref="refForm"
    :rules="rules"
    :model="item"
    status-icon
    label-position="top"
    @keydown.enter="validateForm"
  >
    <div class="c-login-form__heading">Welcome Back</div>

    <section class="c-login-form__items">
      <el-form-item prop="email" @click="emailInput.focus()">
        <label for="email-input"><span class="required-star">*</span> Email</label>
        <input
          id="email-input"
          v-model="item.email"
          type="email"
          ref="emailInput"
          placeholder="abc@spotlight.xyz"
          v-lowercase-email
        />
        <div v-if="error && !item.email" class="error-message">
          Email is required
        </div>
      </el-form-item>

      <el-form-item prop="password" @click="passwordInput.focus()">
        <label for="password-input"><span class="required-star">*</span> Password</label>
        <input
          id="password-input"
          v-model="item.password"
          type="password"
          ref="passwordInput"
        />
        <div v-if="error && !item.password" class="error-message">
          Password is required
        </div>
      </el-form-item>
    </section>

    <el-button
      class="c-login-form__submit"
      type="success"
      @click="validateForm"
      v-loading="loading"
    >
      Login
    </el-button>

    <el-button class="c-login-form__forgot" link @click="gotoForgotPassword">
      <span>Forgot your password?</span>
    </el-button>
  </el-form>
</template>

<style lang="scss">
.c-login-form {
  display: flex;
  flex-direction: column;
  gap: 30px;

  .required-star {
    color: #F56C6C;
  }

  .error-message {
    color: #F56C6C;
    font-size: 12px;
    margin-top: 5px;
    margin-left: -20px;
  }

  &__heading {
    font-family: 'Red Hat Display';
    color: #333333;
    font-size: 30px;
    font-weight: bold;
    text-align: center;
  }

  &__submit.el-button.el-button--success {
    width: 100%;
    height: 48px;
    font-size: 16px;
    border-radius: 56px;
    background-color: #005C6B;
    border-color: #005C6B;
  }

  &__forgot {
    text-align: center;
    width: 100%;

    span {
      color: #005C6B;
      font-weight: 600;
      font-size: 14px;
      text-decoration: underline;
    }
  }

  .el-form-item {
    margin: 0;
  }

  &__items {
    gap: 35px;
    display: flex;
    flex-direction: column;

    .el-form-item__content {
      height: 75px;
      border: 1px solid #DEDEDE;
      border-radius: 10px;
      padding: 20px;
      display: flex;
      flex-direction: column;
      label {
        font-family: "Red Hat Display";
        font-size: 12px;
        color: #666666;
        line-height: 12px;
        font-weight: 400;
      }

      input {
        box-shadow: none;
        border: 0 none transparent;
        outline: none;

        &::placeholder {
          opacity: 0.5;
        }
      }
    }
  }
}
</style> 
